import type { FC } from "react";
import { useIntl } from "@/infra/locale";
import { AppRoutes } from "@/infra/router";
import type { TermsAndConditionsRequest, TermsAndConditionsResponse } from "types/schemas";
import { useFetchLegalPage, useUpdateTermsAndConditionsStatus } from "@akina/infra";
import { Dialog, Text, Button } from "@akina/presentation";
import { LegalExternalLink } from "./LegalExternalLink";

type UpdatedTermsAcceptanceModalProps = {
  open: boolean;
  onOpenChange: (prev: boolean) => void;
  patientTerms: TermsAndConditionsResponse;
};

export const UpdatedTermsAcceptanceModal: FC<UpdatedTermsAcceptanceModalProps> = ({
  open,
  onOpenChange,
  patientTerms,
}) => {
  const { formatMessage } = useIntl();
  const { data: termsAndConditionsLegalPage } = useFetchLegalPage("terms-and-conditions");

  const updateStatus = useUpdateTermsAndConditionsStatus({
    onSuccess: () => {
      onOpenChange(false);
    },
  });

  const handleAccept = () => {
    if (termsAndConditionsLegalPage) {
      const payload: TermsAndConditionsRequest = {
        termsAndConditionsAccepted: true,
        termsAndConditionsAcceptedTimestamp: new Date().toISOString(),
        dataPrivacyPolicyAccepted: patientTerms.dataPrivacyPolicyAccepted,
        dataPrivacyPolicyAcceptedTimestamp: patientTerms.dataPrivacyPolicyAcceptedTimestamp,
        privacyPolicyVersion: patientTerms.privacyPolicyVersion,
        termsVersion: termsAndConditionsLegalPage?.version,
      };
      updateStatus.mutate(payload, {
        onSuccess: () => {
          onOpenChange(false);
        },
      });
    }
  };

  return (
    <Dialog
      withClose={false}
      open={open}
      onOpenChange={isOpen => {
        onOpenChange(isOpen);
      }}
      onInteractOutside={event => event.preventDefault()}
      className="no-scrollbar flex flex-col">
      <Text size="3xl" className="pb-10">
        {formatMessage({
          id: "legal.updatedTermsAcceptance.title",
        })}
      </Text>
      <div className="flex flex-col gap-6">
        <Text size="base">
          {formatMessage({
            id: "legal.updatedTermsAcceptance.description",
          })}
        </Text>
        <div className="mt-2 flex flex-row">
          <div className="flex flex-wrap gap-1">
            <LegalExternalLink route={AppRoutes.TermsAndConditions} messageId="legal.termsOfService" />
          </div>
        </div>

        <Button
          loading={updateStatus.isPending}
          className="ml-auto max-w-fit object-contain text-right"
          onClick={handleAccept}>
          {formatMessage({
            id: "legal.updatedTermsAcceptance.understoodButton",
          })}
        </Button>
      </div>
    </Dialog>
  );
};
